import React, { useMemo, useState } from 'react';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import {
  Box, Button, ButtonGroup, Heading, HStack, IconButton, Menu, MenuButton, MenuDivider, MenuItem,
  MenuList, SimpleGrid, Stack, Stat, StatLabel, StatNumber, Text, useDisclosure, useToast,
} from '@chakra-ui/react';
import {
  FiRefreshCw, FiPlus, FiDownloadCloud, FiMoreVertical, FiArchive,
} from 'react-icons/fi';
import useRecurringRevenueActions from '../hooks/useRecurringRevenueActions';
import AuroraPageShell from '../shared/AuroraPageShell';
import GlassCard from '../shared/GlassCard';
import RecurringRevenueGrid from '../shared/RecurringRevenueGrid';
import RecurringPendingEnrollmentSection from '../shared/RecurringPendingEnrollmentSection';
import InvoiceEmailDeliverySection from '../shared/InvoiceEmailDeliverySection'; // item 1329
import RecurringAutoPaySection from '../shared/RecurringAutoPaySection'; // item 728
import AddRecurringInvoiceWizard from '../shared/AddRecurringInvoiceWizard';
import ImportStripeSubscriptionsModal from '../shared/ImportStripeSubscriptionsModal'; // item 731g
import { useRecurringRevenues, useArchivedRecurringRevenues, usePendingRecurringEnrollments } from '../hooks/useRecurringRevenues';
import { money } from '../utils/money'; // item 761 — stealth-aware
import {
  RecurringRevenue, RecurringRevenueStatus, frequencyLabel, monthlyRecurringValue,
} from '../models/recurringRevenue';
import useAutoReconcile from '../hooks/useAutoReconcile'; // item 1136

type GroupBy = 'service' | 'cadence';

// item 63c — company-wide Recurring Revenue Management, grouped by service sold OR by
// cadence (toggle). Stripe subscriptions are live (test sandbox): creating a schedule issues
// a real Stripe subscription and webhooks sync Active/Cancelled (dart 2026-07-14).
const RecurringRevenuePage: React.FC = () => {
  const { data, status } = useRecurringRevenues();
  useAutoReconcile(); // item 1136 — throttled Stripe reconcile on open
  const [groupBy, setGroupBy] = useState<GroupBy>('service');
  const [showArchived, setShowArchived] = useState(false);
  const { data: archivedData, status: archivedStatus } = useArchivedRecurringRevenues(showArchived);

  const rows = useMemo(() => data?.index ?? [], [data]);
  // item 761 (Chad) — KPI the page: ACTIVE monthly value + PENDING-to-enroll monthly value +
  // the combined total, as stat cards up top (independently visible, not buried in a subtitle).
  const { data: pendingData } = usePendingRecurringEnrollments();
  const pendingMonthly = useMemo(() => (pendingData?.index ?? [])
    .filter((r) => !r.declined)
    .reduce((s2, r) => s2 + (r.monthlyValue ?? 0), 0), [pendingData]);
  const archivedRows = useMemo(() => archivedData?.index ?? [], [archivedData]);

  // item 1131 (Chad) — the /mo cards must NORMALIZE cadence: a quarterly sub contributes a
  // third of its amount per month, annual a twelfth (monthlyRecurringValue, same rule the
  // dashboard MRR / subscriptions card / attrition report already use). Raw summing read a
  // $1,517.04/quarter sub as $1,517.04/mo.
  const activeTotal = useMemo(
    () => rows
      .filter((r) => r.status === RecurringRevenueStatus.Active)
      .reduce((sum, r) => sum + monthlyRecurringValue(r), 0),
    [rows],
  );

  const groups = useMemo(() => {
    const map = new Map<string, RecurringRevenue[]>();
    rows.forEach((r) => {
      const key = groupBy === 'service'
        ? (r.serviceCategory?.trim() || 'Uncategorized')
        : frequencyLabel(r.frequency, r.customIntervalDays);
      const bucket = map.get(key) ?? [];
      bucket.push(r);
      map.set(key, bucket);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows, groupBy]);

  const addWizard = useDisclosure(); // item 448
  const importModal = useDisclosure(); // item 731g
  const toast = useToast();
  const { syncAllFromStripe, isSyncingAll } = useRecurringRevenueActions(); // item 731

  const doSyncAll = async () => {
    try {
      const res = await syncAllFromStripe();
      toast({
        status: 'success',
        title: 'Synced with Stripe',
        description: `${res.reconciled} checked · ${res.cancelled} cancelled (deleted at Stripe).`,
      });
    } catch (e: any) {
      toast({ status: 'error', title: 'Sync failed', description: String(e?.response?.data ?? 'Could not reach Stripe.') });
    }
  };

  return (
    // item 454 — "Recurring revenue" is now presented as Project Subscriptions
    <AuroraPageShell title="PROJECT SUBSCRIPTIONS" icon={<FiRefreshCw color="currentColor" />}>
      <AddRecurringInvoiceWizard isOpen={addWizard.isOpen} onClose={addWizard.onClose} />
      <ImportStripeSubscriptionsModal isOpen={importModal.isOpen} onClose={importModal.onClose} />
      <Stack spacing={4}>
        {/* item 761 (Chad) — the page's KPIs: active vs pending-to-enroll monthly value + combined */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <GlassCard p={4}>
            <Stat>
              <StatLabel opacity={0.7}>Active subscriptions</StatLabel>
              <StatNumber>{`${money(activeTotal)}/mo`}</StatNumber>
              <Text fontSize="xs" opacity={0.6}>{`${rows.filter((r) => r.status === RecurringRevenueStatus.Active).length} active`}</Text>
            </Stat>
          </GlassCard>
          <GlassCard p={4}>
            <Stat>
              <StatLabel opacity={0.7}>Pending enrollment</StatLabel>
              <StatNumber>{`${money(pendingMonthly)}/mo`}</StatNumber>
              <Text fontSize="xs" opacity={0.6}>{`${(pendingData?.index ?? []).filter((r) => !r.declined).length} to enroll`}</Text>
            </Stat>
          </GlassCard>
          <GlassCard p={4}>
            <Stat>
              <StatLabel opacity={0.7}>Total (active + pending)</StatLabel>
              <StatNumber>{`${money(activeTotal + pendingMonthly)}/mo`}</StatNumber>
              <Text fontSize="xs" opacity={0.6}>once everything is enrolled</Text>
            </Stat>
          </GlassCard>
        </SimpleGrid>
        {/* item 1329 — did the invoice emails actually go out? Basix is the only sender since
            1326, so this is the receipt AND the nag. Quiet one-liner when all is well. */}
        <InvoiceEmailDeliverySection />
        {/* item 706 — sold-but-not-enrolled subscriptions live here now (not buried in an invoice) */}
        <RecurringPendingEnrollmentSection />
        {/* item 728 — active subscriptions not yet on auto-pay (opt-in enrollment) */}
        <RecurringAutoPaySection rows={rows} />
        {/* item 731 (Chad) — ONE cohesive Subscriptions block: the metric + controls are the
            header OF the grid they drive (no floating control card, no redundant grid title). */}
        <GlassCard p={{ base: 4, md: 6 }}>
          <HStack justify="space-between" align="flex-start" wrap="wrap" spacing={4} mb={5}>
            <Box>
              <Heading size="md">Subscriptions</Heading>
              <Text fontSize="sm" color="gray.500" mt={1}>
                {`${activeTotal.toLocaleString(undefined, { style: 'currency', currency: 'USD' })} active value · ${rows.filter((r) => r.status === RecurringRevenueStatus.Active).length} active · ${rows.length} total`}
              </Text>
            </Box>
            <HStack spacing={3} wrap="wrap" align="flex-end">
              <Box>
                <Text fontSize="xs" color="gray.500" mb={1}>Group by</Text>
                <ButtonGroup size="sm" isAttached variant="outline">
                  <Button
                    colorScheme={groupBy === 'service' ? 'brandGreen' : 'gray'}
                    variant={groupBy === 'service' ? 'solid' : 'outline'}
                    onClick={() => setGroupBy('service')}
                  >
                    Service
                  </Button>
                  <Button
                    colorScheme={groupBy === 'cadence' ? 'brandGreen' : 'gray'}
                    variant={groupBy === 'cadence' ? 'solid' : 'outline'}
                    onClick={() => setGroupBy('cadence')}
                  >
                    Cadence
                  </Button>
                </ButtonGroup>
              </Box>
              {/* item 448 — create a subscription from scratch (the only entry point) */}
              <Button size="sm" colorScheme="brandGreen" leftIcon={<FiPlus />} onClick={addWizard.onOpen}>
                Add subscription
              </Button>
              <Menu>
                <MenuButton
                  as={IconButton}
                  size="sm"
                  variant="outline"
                  aria-label="More subscription actions"
                  icon={<FiMoreVertical />}
                />
                <MenuList>
                  {/* item 731g — adopt existing live Stripe subscriptions (dry-run first) */}
                  <MenuItem icon={<FiDownloadCloud />} onClick={importModal.onOpen}>
                    Import from Stripe
                  </MenuItem>
                  {/* item 731 — reconcile all subs with Stripe (deleted → Cancelled) */}
                  <MenuItem icon={<FiRefreshCw />} onClick={doSyncAll} isDisabled={isSyncingAll}>
                    {isSyncingAll ? 'Syncing…' : 'Sync all from Stripe'}
                  </MenuItem>
                  <MenuDivider />
                  {/* item 170 — the archived view (never deleted, just hidden) */}
                  <MenuItem icon={<FiArchive />} onClick={() => setShowArchived((v) => !v)}>
                    {showArchived ? 'Hide archived' : 'Show archived'}
                  </MenuItem>
                </MenuList>
              </Menu>
            </HStack>
          </HStack>

          {status === 'success' && rows.length === 0 && (
            <Text color="gray.500">
              No subscriptions yet. Click ADD SUBSCRIPTION above to create one, or IMPORT FROM STRIPE.
            </Text>
          )}

          <Stack spacing={5}>
            {groups.map(([key, groupRows]) => (
              <Box key={key}>
                {/* only label a group when the grouping actually says something */}
                {(groups.length > 1 || key !== 'Uncategorized') && (
                  <Heading size="xs" color="gray.500" mb={2} textTransform="uppercase" letterSpacing="0.08em">
                    {key}
                  </Heading>
                )}
                <RecurringRevenueGrid data={groupRows} status={status} showProjectColumns embedded />
              </Box>
            ))}

            {status === 'loading' && (
              <RecurringRevenueGrid data={undefined} status={status} showProjectColumns embedded />
            )}

            {/* item 170 — archived schedules (restore-only) */}
            {showArchived && (
              <Box>
                <Heading size="xs" color="gray.500" mb={2} textTransform="uppercase" letterSpacing="0.08em">
                  Archived
                </Heading>
                <RecurringRevenueGrid
                  data={archivedRows}
                  status={archivedStatus}
                  showProjectColumns
                  embedded
                  embeddedEmptyText="No archived schedules."
                />
              </Box>
            )}
          </Stack>
        </GlassCard>
      </Stack>
    </AuroraPageShell>
  );
};

export default withAuthenticationRequired(RecurringRevenuePage);
